import {CertificatRamonageDocumentSchema, getCustomerFilenameSlug} from "@mytour/shared";
import type {
  CertificatRamonageDetails,
  CertificatRamonageDocument,
  CertificatRamonagePayload,
  CertificatRamonagePrefill,
  DownloadCertificatRamonageResponse,
  ICustomer,
  SaveCertificatRamonageResponse,
} from "@mytour/shared";
import axios from "axios";
import dayjs from "dayjs";

export const certificateApiService = {
  async getCertificate(eventId: string): Promise<CertificatRamonageDetails | null> {
    const result = await axios.get<CertificatRamonageDetails | undefined>(
      `/api/chimney-sweep-certificate/${eventId}`
    );
    return result.data ?? null;
  },

  async getLatestCertificateByCustomer(
    customerId: string
  ): Promise<CertificatRamonagePrefill | null> {
    const result = await axios.get<CertificatRamonagePrefill | undefined>(
      `/api/chimney-sweep-certificate/customer/${customerId}/latest`
    );
    return result.data ?? null;
  },

  async listDocumentsByCustomer(customerId: string): Promise<CertificatRamonageDocument[]> {
    const result = await axios.get<unknown[]>(
      `/api/chimney-sweep-certificate/customer/${customerId}`
    );
    /**
     * `attestationDate`/`eventDate` arrivent en string ISO — le schéma coerce en Date.
     * Parsée ligne à ligne : une ligne non conforme est écartée sans faire échouer les autres
     * (la query de liste n'expose pas `error`, cf. `useCertificatRamonageDocumentsByCustomer`).
     */
    return (result.data ?? [])
      .map((raw) => CertificatRamonageDocumentSchema.safeParse(raw))
      .filter(
        (parsed): parsed is {success: true; data: CertificatRamonageDocument} => parsed.success
      )
      .map((parsed) => parsed.data);
  },

  async saveCertificate(
    eventId: string,
    payload: CertificatRamonagePayload
  ): Promise<CertificatRamonageDetails> {
    const result = await axios.post<CertificatRamonageDetails>(
      `/api/chimney-sweep-certificate/${eventId}`,
      payload
    );
    return result.data;
  },

  async uploadFile(
    eventId: string,
    blob: Blob,
    sendEmail: boolean,
    customer: ICustomer
  ): Promise<SaveCertificatRamonageResponse> {
    const formData = new FormData();
    const parsedDate = dayjs().format("YYYY-MM-DD");
    const fileName = `attestation-ramonage_${getCustomerFilenameSlug(customer)}_${parsedDate}.pdf`;
    formData.append("file", new File([blob], fileName, {type: "application/pdf"}));
    formData.append("sendEmail", JSON.stringify(sendEmail));
    const result = await axios.post<SaveCertificatRamonageResponse>(
      `/api/chimney-sweep-certificate/${eventId}/file`,
      formData,
      {headers: {"Content-Type": "multipart/form-data"}}
    );
    return result.data;
  },

  async downloadFile(eventId: string): Promise<DownloadCertificatRamonageResponse> {
    const result = await axios.get<ArrayBuffer>(`/api/chimney-sweep-certificate/${eventId}/file`, {
      responseType: "arraybuffer",
    });
    return {
      blob: new Blob([result.data], {type: "application/pdf"}),
      fileName: getDownloadFileName(result.headers["content-disposition"]),
    };
  },

  async resend(
    eventId: string
  ): Promise<Pick<SaveCertificatRamonageResponse, "emailStatus" | "emailMessage">> {
    const result = await axios.post<
      Pick<SaveCertificatRamonageResponse, "emailStatus" | "emailMessage">
    >(`/api/chimney-sweep-certificate/${eventId}/resend`);
    return result.data;
  },
};

function getDownloadFileName(contentDisposition: unknown): string | undefined {
  if (typeof contentDisposition !== "string") {
    return undefined;
  }
  const encodedName = /(?:^|;)\s*filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  if (encodedName) {
    try {
      return decodeURIComponent(encodedName.trim());
    } catch {}
  }
  const match = /(?:^|;)\s*filename=(?:"((?:\\.|[^"\\])*)"|([^;]+))/i.exec(contentDisposition);
  return match?.[1]?.replace(/\\(.)/g, "$1") || match?.[2]?.trim() || undefined;
}
